import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, MapPin, Clock, Users } from "lucide-react";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { publishedFormations, sessionRemaining } from "@/components/site/Formations";

const TYPE_COLOR = { Tournage: "#3E9BFF", Atelier: "#34d399", Formation: "#34d399", "Événement": "#fbbf24", Festival: "#f472b6" };
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

// Build a normalized list of dated events from events + formation sessions + festival
const buildAgenda = (content) => {
  const out = [];
  (content.events?.items || []).filter((e) => (e.status || "published") !== "hidden" && e.date).forEach((e) => {
    out.push({ id: e.id, date: e.date, endDate: e.endDate, time: e.time, title: e.title, type: e.type || "Événement", location: e.location, spots: Number(e.spotsTotal) ? Math.max(0, Number(e.spotsTotal) - Number(e.spotsTaken || 0)) : null });
  });
  publishedFormations(content.formations || {}).forEach((f) => {
    (f.sessions || []).filter((s) => s.date).forEach((s) => {
      out.push({ id: `${f.id}-${s.id}`, date: s.date, time: s.time, title: f.title, type: "Formation", location: s.location, spots: sessionRemaining(s), href: `/formation/${f.slug || f.id}` });
    });
  });
  const fest = content.festival || {};
  if (fest.startDate) out.push({ id: "festival", date: fest.startDate, endDate: fest.endDate, title: `${fest.titlePrefix || "Karams"} ${fest.titleAccent || "Festival"} — ${fest.edition || ""}`.trim(), type: "Festival", location: fest.place, href: "/festival" });
  return out.sort((a, b) => a.date.localeCompare(b.date));
};

const CalendarBody = () => {
  const { content } = useContent();
  const agenda = useMemo(() => buildAgenda(content), [content]);
  const ev = content.events || {};
  const [cursor, setCursor] = useState(() => { const d = agenda[0] ? new Date(agenda[0].date + "T00:00:00") : new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const byDay = useMemo(() => {
    const m = {};
    agenda.forEach((e) => {
      // expand multi-day events
      const start = new Date(e.date + "T00:00:00");
      const end = e.endDate ? new Date(e.endDate + "T00:00:00") : start;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().slice(0, 10);
        (m[key] = m[key] || []).push(e);
      }
    });
    return m;
  }, [agenda]);

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const monthAgenda = agenda.filter((e) => { const d = new Date(e.date + "T00:00:00"); return d.getFullYear() === year && d.getMonth() === month; });
  const todayKey = new Date().toISOString().slice(0, 10);

  return (
    <div className="App bg-[#050505] min-h-screen flex flex-col">
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <Navbar />
      <main className="flex-1 pt-32 md:pt-40 pb-24" data-testid="calendar-page">
        <div className="max-w-[1300px] mx-auto px-6 md:px-12">
          <Reveal className="mb-12 max-w-2xl">
            <Link to="/" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-secondary2 hover:text-electric transition-colors mb-8"><ArrowLeft size={14} /> Accueil</Link>
            <div className="flex items-center gap-3 mb-5"><span className="h-px w-10 bg-electric" /><span className="text-xs tracking-[0.35em] uppercase text-electric">{ev.eyebrow || "Agenda"}</span></div>
            <h1 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95] mb-6">{ev.heading || "Le calendrier de l'association."}</h1>
            {ev.intro && <p className="text-base md:text-lg text-secondary2 font-light leading-relaxed">{ev.intro}</p>}
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-10">
            {/* Month grid */}
            <Reveal>
              <div className="rounded-xl border border-white/10 bg-surface/40 p-5 md:p-6">
                <div className="flex items-center justify-between mb-5">
                  <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="h-9 w-9 rounded-full border border-white/15 text-white flex items-center justify-center hover:border-electric hover:text-electric transition-colors"><ChevronLeft size={18} /></button>
                  <div className="font-serif text-2xl text-white">{MONTHS[month]} {year}</div>
                  <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="h-9 w-9 rounded-full border border-white/15 text-white flex items-center justify-center hover:border-electric hover:text-electric transition-colors"><ChevronRight size={18} /></button>
                </div>
                <div className="grid grid-cols-7 gap-1 mb-2">{DAYS.map((d) => <div key={d} className="text-center text-[10px] tracking-[0.15em] uppercase text-secondary2 py-1">{d}</div>)}</div>
                <div className="grid grid-cols-7 gap-1">
                  {cells.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const key = d.toISOString().slice(0, 10);
                    const evs = byDay[key] || [];
                    const isToday = key === todayKey;
                    return (
                      <div key={i} className={`aspect-square rounded-lg border p-1.5 flex flex-col ${isToday ? "border-electric/60 bg-electric/5" : "border-white/5"} ${evs.length ? "bg-white/[0.03]" : ""}`}>
                        <span className={`text-xs ${isToday ? "text-electric font-semibold" : "text-white/70"}`}>{d.getDate()}</span>
                        <div className="mt-auto flex flex-wrap gap-0.5">
                          {evs.slice(0, 3).map((e, x) => <span key={x} className="h-1.5 w-1.5 rounded-full" style={{ background: TYPE_COLOR[e.type] || "#3E9BFF" }} title={e.title} />)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>

            {/* Agenda list for the month */}
            <Reveal delay={0.1}>
              <h2 className="font-serif text-2xl text-white mb-5">Ce mois-ci</h2>
              {monthAgenda.length === 0 ? (
                <p className="text-secondary2 text-sm">Aucun événement en {MONTHS[month]} {year}.</p>
              ) : (
                <div className="space-y-3">
                  {monthAgenda.map((e, i) => {
                    const d = new Date(e.date + "T00:00:00");
                    const Wrapper = e.href ? Link : "div";
                    return (
                      <Wrapper key={e.id || i} {...(e.href ? { to: e.href } : {})} data-testid={`agenda-item-${i}`} className={`flex gap-4 rounded-lg border border-white/10 bg-[#0a0a0a] p-4 ${e.href ? "hover:border-electric/40 transition-colors block" : ""}`}>
                        <div className="text-center min-w-[48px]">
                          <div className="text-2xl font-serif text-white leading-none">{d.getDate()}</div>
                          <div className="text-[10px] tracking-[0.15em] uppercase text-secondary2 mt-1">{MONTHS[d.getMonth()].slice(0, 3)}</div>
                        </div>
                        <div className="border-l pl-4 flex-1" style={{ borderColor: (TYPE_COLOR[e.type] || "#3E9BFF") + "66" }}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full" style={{ color: TYPE_COLOR[e.type] || "#3E9BFF", border: `1px solid ${(TYPE_COLOR[e.type] || "#3E9BFF")}55` }}>{e.type}</span>
                          </div>
                          <div className="font-serif text-lg text-white leading-tight">{e.title}</div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-secondary2">
                            {e.time && <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {e.time}</span>}
                            {e.location && <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {e.location}</span>}
                            {e.spots != null && <span className={`inline-flex items-center gap-1.5 ${e.spots === 0 ? "text-red-400" : "text-electric"}`}><Users size={12} /> {e.spots === 0 ? "Complet" : `${e.spots} places`}</span>}
                          </div>
                        </div>
                      </Wrapper>
                    );
                  })}
                </div>
              )}
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default function CalendarPage() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <Analytics />
        <CalendarBody />
      </ReactLenis>
    </ContentProvider>
  );
}
